import { Shape, WallPathEdge, WallPathShape, WallShape } from "../types";
import { generateShapeId } from "./generateShapeId";
import { computeWallPathBounds } from "./shapeFactory";
import { WallSegmentSnap } from "./snap";

/** A resolved attach point on the interior of an existing wall segment. */
export interface WallAttachPoint extends WallSegmentSnap {
  x: number;
  y: number;
}

/**
 * Splits doors/windows of an edge that is being cut at ratio `t`, remapping
 * each opening's 0–1 position into the half it falls on. Openings keep their
 * pixel width, so only the position ratio needs rescaling.
 */
const splitOpenings = <T extends { position: number }>(
  openings: T[] | undefined,
  t: number
): { before: T[] | undefined; after: T[] | undefined } => {
  if (!openings || openings.length === 0) {
    return { before: undefined, after: undefined };
  }
  const before: T[] = [];
  const after: T[] = [];
  for (const opening of openings) {
    if (opening.position <= t) {
      before.push({ ...opening, position: opening.position / t });
    } else {
      after.push({ ...opening, position: (opening.position - t) / (1 - t) });
    }
  }
  return {
    before: before.length > 0 ? before : undefined,
    after: after.length > 0 ? after : undefined
  };
};

const splitWallPathAtPoint = (
  shapes: Shape[],
  path: WallPathShape,
  hit: WallAttachPoint
): Shape[] => {
  const edgeCount = path.closed
    ? path.vertices.length
    : path.vertices.length - 1;
  if (hit.edgeIndex < 0 || hit.edgeIndex >= edgeCount) return shapes;

  const splitEdge = path.edges[hit.edgeIndex] ?? {};
  const doors = splitOpenings(splitEdge.doors, hit.t);
  const windows = splitOpenings(splitEdge.windows, hit.t);
  const edgeA: WallPathEdge = { doors: doors.before, windows: windows.before };
  const edgeB: WallPathEdge = { doors: doors.after, windows: windows.after };

  // Works for the closing edge too: slice(0, edgeCount) is the whole
  // vertex list, so the new vertex is appended at the end.
  const newVertices = [
    ...path.vertices.slice(0, hit.edgeIndex + 1),
    { x: hit.x, y: hit.y },
    ...path.vertices.slice(hit.edgeIndex + 1)
  ];
  const newEdges = [
    ...path.edges.slice(0, hit.edgeIndex),
    edgeA,
    edgeB,
    ...path.edges.slice(hit.edgeIndex + 1)
  ];
  const bounds = computeWallPathBounds(newVertices);

  return shapes.map((s) =>
    s.id === path.id
      ? { ...path, vertices: newVertices, edges: newEdges, ...bounds }
      : s
  );
};

const splitLegacyWallAtPoint = (
  shapes: Shape[],
  wall: WallShape,
  hit: WallAttachPoint
): Shape[] => {
  // Curved walls are never segment-snap targets; guard anyway
  if (wall.curvature) return shapes;

  const doors = splitOpenings(wall.doors, hit.t);
  const windows = splitOpenings(wall.windows, hit.t);

  // The first half keeps the original id so references/selection survive
  const firstHalf: WallShape = {
    ...wall,
    x2: hit.x,
    y2: hit.y,
    doors: doors.before,
    windows: windows.before,
    lineCapEnd: "butt"
  };
  const secondHalf: WallShape = {
    ...wall,
    id: generateShapeId("wall"),
    x: hit.x,
    y: hit.y,
    doors: doors.after,
    windows: windows.after,
    lineCapStart: "butt"
  };

  return shapes.flatMap((s) =>
    s.id === wall.id ? [firstHalf, secondHalf] : [s]
  );
};

/**
 * Turns an interior point of an existing wall into a real vertex so another
 * wall can attach there (T-junction). For a wall-path the vertex is inserted
 * into the host's edge; a legacy wall is split into two walls meeting at the
 * point. Doors/windows on the split edge are redistributed to the matching
 * half. Returns the shapes array unchanged when the hit cannot be resolved.
 */
export const splitWallAtPoint = (
  shapes: Shape[],
  hit: WallAttachPoint
): Shape[] => {
  const host = shapes.find((s) => s.id === hit.shapeId);
  if (!host) return shapes;
  if (hit.t <= 0 || hit.t >= 1) return shapes;

  if (host.type === "wall-path") {
    return splitWallPathAtPoint(shapes, host as WallPathShape, hit);
  }
  if (host.type === "wall") {
    return splitLegacyWallAtPoint(shapes, host as WallShape, hit);
  }
  return shapes;
};
